import React, { useEffect, useMemo, useState } from "react";
import { Devices } from "@app/shared/src/api/api.gen";
import { ChirpConfig, Device, FleetDefaults, TrackerConfig, TrackerConfigType } from "@app/shared/src/models";
import { Btn, Card, CardNote, CardHead, FRow, FVal, Modal, Note, Select, Tag, useToast } from "@app/shared/src/components_v2";
import { formatBitrate } from "@app/shared/src/helpers/numberHelpers";
import {
    RETENTION_DAY_CHOICES,
    RadarVideoSettings,
    STREAM_PROFILES,
    VIDEO_PROFILES,
    applyRadarVideo,
    matchProfile,
    radarVideoDifferences,
    radarVideoFromDefaults,
    radarVideoMatches,
    readRadarVideo,
    trackersOfType,
} from "../../helpers/radarVideoSettings";
import { InheritedSection } from "../shared/InheritedSection";
import { SaveBar } from "../shared/SaveBar";
import { Toggle } from "../shared/Toggle";

/**
 * Radar and video for one truck.
 *
 * Every value here lives on the device row, so committing is a single save and "reset to the fleet
 * default" is the same write with different numbers — which is the whole reason this section is the
 * cleanest illustration of the inheritance contract. Whether it shows as inherited is decided by
 * comparing all fourteen fields against the org's defaults, not by a stored flag, so the badge can
 * never disagree with the values printed underneath it.
 */

/** Select value standing for "this unit's own hand-authored config" — never a real preset id. */
const CUSTOM_OPTION = "custom";

const TRACKER_KEYS = {
    radar: { id: "radarTrackerConfigId", custom: "radarTrackerCustomConfig" },
    video: { id: "videoTrackerConfigId", custom: "videoTrackerCustomConfig" },
} as const;

const FIELD_LABELS: Partial<Record<keyof RadarVideoSettings, string>> = {
    radarEnabled: "radar transmitter",
    chirpConfigId: "radar profile",
    radarTrackerConfigId: "radar tracker",
    videoTrackerConfigId: "camera tracker",
    dvrRecordingEnabled: "DVR recording",
    dvrResolutionX: "recording profile",
    dvrResolutionY: "recording profile",
    dvrFps: "recording profile",
    dvrBitrateBps: "recording profile",
    dvrRetentionHours: "retention",
    streamResolutionX: "streaming profile",
    streamResolutionY: "streaming profile",
    streamFps: "streaming profile",
    streamBitrateBps: "streaming profile",
};

export const RadarVideoSection: React.FC<{
    device: Device;
    defaults?: FleetDefaults;
    chirpConfigs: ChirpConfig[];
    trackerConfigs: TrackerConfig[];
    onSaved: () => void;
}> = ({ device, defaults, chirpConfigs, trackerConfigs, onSaved }) => {
    const toast = useToast();
    const saved = useMemo(() => readRadarVideo(device), [device]);
    const [draft, setDraft] = useState<RadarVideoSettings>(saved);
    const [busy, setBusy] = useState(false);
    const [overridden, setOverridden] = useState(false);
    /** The custom-JSON dialog: which tracker it edits, and the text as typed. */
    const [customEditor, setCustomEditor] = useState<{ kind: "radar" | "video"; text: string; error?: string }>();

    useEffect(() => setDraft(saved), [saved]);

    const fleet = defaults ? radarVideoFromDefaults(defaults) : undefined;
    /** No sensor config means every radar field is unreadable and unwritable — say so, don't fake a toggle. */
    const radarReadable = saved.radarEnabled !== undefined;
    const inherited = fleet ? radarVideoMatches(saved, fleet) && !overridden : false;
    const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

    const radarTrackers = useMemo(() => trackersOfType(trackerConfigs, TrackerConfigType.Radar), [trackerConfigs]);
    const videoTrackers = useMemo(() => trackersOfType(trackerConfigs, TrackerConfigType.Video), [trackerConfigs]);

    const differences = fleet
        ? Array.from(new Set(radarVideoDifferences(saved, fleet).map((k) => FIELD_LABELS[k] ?? k)))
        : [];

    const set = <K extends keyof RadarVideoSettings>(key: K, value: RadarVideoSettings[K]) =>
        setDraft((d) => ({ ...d, [key]: value }));

    const commit = async (next: RadarVideoSettings, message: string) => {
        setBusy(true);
        try {
            applyRadarVideo(device, next, { chirpConfigs, trackerConfigs });
            await Devices.save(device);
            toast(message);
            setOverridden(false);
            onSaved();
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not save that");
        } finally {
            setBusy(false);
        }
    };

    const recording = matchProfile(VIDEO_PROFILES, draft.dvrResolutionX, draft.dvrResolutionY, draft.dvrFps);
    const streaming = matchProfile(STREAM_PROFILES, draft.streamResolutionX, draft.streamResolutionY, draft.streamFps);

    const applyCustomEdit = () => {
        if (!customEditor) return;
        // A tracker config is a settings object; bare values and arrays parse as JSON but would
        // reach the unit as nonsense, so require the object shape here.
        let parsed: unknown;
        try {
            parsed = JSON.parse(customEditor.text);
        } catch {
            parsed = undefined;
        }
        if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
            setCustomEditor({ ...customEditor, error: "This must be a JSON object — the unit parses it directly." });
            return;
        }
        set(TRACKER_KEYS[customEditor.kind].custom, customEditor.text);
        setCustomEditor(undefined);
    };

    /**
     * One tracker picker. The list is split by type — a radar tracker slot only accepts radar
     * profiles — and a unit running a hand-authored config shows it as "Custom" (openable in a
     * dialog) rather than pretending nothing is set. A choice that is not in the offered list —
     * a profile of the wrong type picked before the lists were split, or one since deleted — is
     * still shown, disabled, so the select cannot silently display the wrong current state.
     */
    const trackerRow = (kind: "radar" | "video") => {
        const keys = TRACKER_KEYS[kind];
        const options = kind === "radar" ? radarTrackers : videoTrackers;
        const id = draft[keys.id];
        const custom = draft[keys.custom];
        const stray = id !== undefined && !options.some((t) => t.id === id);
        const strayName = stray ? trackerConfigs.find((t) => t.id === id)?.name : undefined;
        return (
            <FRow stack title={kind === "radar" ? "Radar tracker" : "Camera tracker"}>
                <span className="flex items-center gap-2">
                    <Select
                        value={id ?? (custom !== undefined ? CUSTOM_OPTION : "")}
                        aria-label={kind === "radar" ? "Radar tracker profile" : "Camera tracker profile"}
                        onChange={(e) => set(keys.id, e.target.value === CUSTOM_OPTION ? undefined : e.target.value || undefined)}
                    >
                        {custom !== undefined && <option value={CUSTOM_OPTION}>Custom</option>}
                        {custom === undefined && id === undefined && <option value="">Not set fleet-wide</option>}
                        {stray && (
                            <option value={id} disabled>
                                {strayName ? `${strayName} · not a ${kind === "radar" ? "radar" : "camera"} tracker` : "Unknown profile"}
                            </option>
                        )}
                        {options.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                    </Select>
                    {custom !== undefined && id === undefined && (
                        <Btn tone="quiet" onClick={() => setCustomEditor({ kind, text: custom })}>
                            View / edit
                        </Btn>
                    )}
                </span>
            </FRow>
        );
    };

    return (
        <>
            <InheritedSection
                title="Radar & video"
                inherited={inherited}
                onOverride={() => setOverridden(true)}
                onResetToDefault={fleet && !inherited ? () => commit(fleet, `${device.name} is back on the fleet default`) : undefined}
                differences={differences}
            >
                <div className="grid gap-4 lg:grid-cols-2">
                    <Card>
                        <CardHead title="Radar" />
                        {radarReadable ? (
                            <>
                                <div className="mt-2">
                                    <FRow title="Radar enabled" sub="The transmitter itself — off means this truck detects nothing">
                                        <Toggle
                                            on={!!draft.radarEnabled}
                                            onChange={(v) => set("radarEnabled", v)}
                                            label="Radar enabled"
                                        />
                                    </FRow>
                                    <FRow stack title="Radar profile" sub="Chirp configuration this unit runs">
                                        <Select
                                            value={draft.chirpConfigId ?? ""}
                                            onChange={(e) => set("chirpConfigId", e.target.value || undefined)}
                                        >
                                            {draft.chirpConfigId === undefined && (
                                                <option value="">Not set fleet-wide</option>
                                            )}
                                            {chirpConfigs.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </FRow>
                                    {trackerRow("radar")}
                                    {trackerRow("video")}
                                </div>
                                <CardNote>
                                    Changing the radar profile re-tunes detection on this truck alone. To move the whole
                                    fleet, use Settings → Radar &amp; video. A tracker shown as{" "}
                                    <b className="font-semibold text-[var(--text)]">Custom</b> is hand-authored for this
                                    unit; choosing a named profile replaces it.
                                </CardNote>
                            </>
                        ) : (
                            <div className="mt-2">
                                <Note>
                                    No radar configuration on record for this unit, so its transmitter, chirp profile and
                                    trackers cannot be read or changed here. A radar unit reports one when it first
                                    checks in.
                                </Note>
                            </div>
                        )}
                    </Card>

                    <Card>
                        <CardHead title="Video" />
                        <div className="mt-2">
                            <FRow title="DVR recording" sub="Continuous recording to the unit's disk">
                                <Toggle
                                    on={draft.dvrRecordingEnabled}
                                    onChange={(v) => set("dvrRecordingEnabled", v)}
                                    label="DVR recording"
                                />
                            </FRow>
                            <FRow stack title="Recording profile" sub="Higher quality fills the disk sooner">
                                <Select
                                    value={recording?.label ?? ""}
                                    onChange={(e) => {
                                        const p = VIDEO_PROFILES.find((x) => x.label === e.target.value);
                                        if (!p) return;
                                        setDraft((d) => ({
                                            ...d,
                                            dvrResolutionX: p.width,
                                            dvrResolutionY: p.height,
                                            dvrFps: p.fps,
                                            dvrBitrateBps: p.bitrateBps,
                                        }));
                                    }}
                                >
                                    {!recording && (
                                        <option value="">
                                            Custom · {draft.dvrResolutionX}×{draft.dvrResolutionY} · {draft.dvrFps} fps
                                        </option>
                                    )}
                                    {VIDEO_PROFILES.map((p) => (
                                        <option key={p.label} value={p.label}>
                                            {p.label}
                                        </option>
                                    ))}
                                </Select>
                            </FRow>
                            <FRow title="Retention" sub="A full disk overwrites the oldest clips first, so this is a floor">
                                <Select
                                    value={String(Math.round(draft.dvrRetentionHours / 24))}
                                    onChange={(e) => set("dvrRetentionHours", Number(e.target.value) * 24)}
                                >
                                    {!RETENTION_DAY_CHOICES.includes(Math.round(draft.dvrRetentionHours / 24)) && (
                                        <option value={String(Math.round(draft.dvrRetentionHours / 24))}>
                                            {Math.round(draft.dvrRetentionHours / 24)} days
                                        </option>
                                    )}
                                    {RETENTION_DAY_CHOICES.map((d) => (
                                        <option key={d} value={d}>
                                            {d} days
                                        </option>
                                    ))}
                                </Select>
                            </FRow>
                            <FRow stack title="Streaming profile" sub="Lower uses less of the truck's data plan">
                                <Select
                                    value={streaming?.label ?? ""}
                                    onChange={(e) => {
                                        const p = STREAM_PROFILES.find((x) => x.label === e.target.value);
                                        if (!p) return;
                                        setDraft((d) => ({
                                            ...d,
                                            streamResolutionX: p.width,
                                            streamResolutionY: p.height,
                                            streamFps: p.fps,
                                            streamBitrateBps: p.bitrateBps,
                                        }));
                                    }}
                                >
                                    {!streaming && (
                                        <option value="">
                                            Custom · {draft.streamResolutionX}×{draft.streamResolutionY} · {draft.streamFps} fps
                                        </option>
                                    )}
                                    {STREAM_PROFILES.map((p) => (
                                        <option key={p.label} value={p.label}>
                                            {p.label}
                                        </option>
                                    ))}
                                </Select>
                            </FRow>
                            <FRow title="Disk">
                                <FVal mu>{formatBitrate(draft.dvrBitrateBps)}</FVal>
                                <Tag tone="mute">recording bitrate</Tag>
                            </FRow>
                        </div>
                        <CardNote>Changes reach the unit on its next heartbeat.</CardNote>
                    </Card>
                </div>
            </InheritedSection>

            {customEditor && (
                <Modal
                    open
                    onClose={() => setCustomEditor(undefined)}
                    title={customEditor.kind === "radar" ? "Custom radar tracker" : "Custom camera tracker"}
                    subtitle={`Hand-authored configuration this unit runs instead of a shared profile. Edits reach ${device.name} when the section is saved.`}
                    footer={
                        <>
                            <Btn tone="quiet" onClick={() => setCustomEditor(undefined)}>
                                Cancel
                            </Btn>
                            <Btn onClick={applyCustomEdit}>Use this config</Btn>
                        </>
                    }
                >
                    <textarea
                        value={customEditor.text}
                        onChange={(e) => setCustomEditor({ ...customEditor, text: e.target.value, error: undefined })}
                        rows={14}
                        spellCheck={false}
                        aria-label={`Custom ${customEditor.kind === "radar" ? "radar" : "camera"} tracker configuration JSON`}
                        className="w-full resize-y rounded-[10px] border border-[var(--line-2)] bg-[var(--surface-2)] p-3 font-mono text-[12px] leading-relaxed"
                    />
                    {customEditor.error && <Note tone="crit">{customEditor.error}</Note>}
                    <p className="text-[12px] leading-relaxed text-[var(--muted)]">
                        To stop running a custom configuration, close this and pick a named profile instead.
                    </p>
                </Modal>
            )}

            <SaveBar
                show={dirty && !inherited}
                busy={busy}
                label={`Unsaved · ${device.name}`}
                onSave={() => commit(draft, `Radar & video saved to ${device.name}`)}
                onDiscard={() => setDraft(saved)}
            />
        </>
    );
};

